/**
 * Runtime settings layer.
 *
 * Several UI flags used to be read straight from `process.env.REACT_APP_*` at
 * module load (baked in at build time, only changeable by editing `.env` and
 * restarting). They are now resolved as:
 *
 *     localStorage override  ??  .env default  ??  built-in default
 *
 * so a value can be overridden at runtime by writing to the localStorage key
 * below without touching `.env`. On the next page load the module-level
 * constants that call into here (FREE_MODE, THEME, …) re-initialise with the
 * new value.
 *
 * Consumers keep importing the same constants as before — only the constant
 * definitions in `i18n.ts` / `productDefs.ts` changed to read through here.
 *
 * NB. CRA only inlines `process.env.REACT_APP_X` when it is a *static* member
 * access, so the env defaults are read statically into `ENV_DEFAULTS` rather
 * than via a dynamic `process.env[key]` lookup.
 */

export const SETTINGS_STORAGE_KEY = 'rush-machine-settings-v1';

export type SettingType = 'bool' | 'enum';

export type SettingDescriptor = {
  /** Stable id — used as the localStorage override key. */
  key: string;
  /** The `.env` variable this falls back to. */
  envVar: string;
  type: SettingType;
  /** Allowed values for an `enum` setting. */
  options?: readonly string[];
  /** Built-in fallback used when neither an override nor the env var is set. */
  default: string;
  label: string;
  help?: string;
};

export const SETTING_DESCRIPTORS: readonly SettingDescriptor[] = [
  { key: 'lang', envVar: 'REACT_APP_LANG', type: 'enum', options: ['en', 'tr'], default: 'en', label: 'Language', help: 'UI language.' },
  { key: 'theme', envVar: 'REACT_APP_THEME', type: 'enum', options: ['rush', 'luxe'], default: 'rush', label: 'Theme', help: 'rush = green/black tech · luxe = warm gold.' },
  { key: 'halalMode', envVar: 'REACT_APP_HALAL_MODE', type: 'bool', default: 'false', label: 'Halal menu', help: 'Use the no-alcohol catalog.' },
  { key: 'freeMode', envVar: 'REACT_APP_FREE_MODE', type: 'bool', default: 'false', label: 'Free mode', help: 'Hide all prices and skip the payment step.' },
  { key: 'uxAnalytics', envVar: 'REACT_APP_UX_ANALYTICS', type: 'bool', default: 'false', label: 'UX analytics', help: 'Record click positions and page dwell times.' },
] as const;

/** Static env reads — see the CRA note above. Keyed by setting `key`. */
const ENV_DEFAULTS: Record<string, string | undefined> = {
  lang: process.env.REACT_APP_LANG,
  theme: process.env.REACT_APP_THEME,
  halalMode: process.env.REACT_APP_HALAL_MODE,
  freeMode: process.env.REACT_APP_FREE_MODE,
  uxAnalytics: process.env.REACT_APP_UX_ANALYTICS,
};

type Overrides = Record<string, string>;

const byKey = (key: string): SettingDescriptor | undefined =>
  SETTING_DESCRIPTORS.find((d) => d.key === key);

function loadOverrides (): Overrides {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Overrides) : {};
  } catch {
    return {};
  }
}

// Cached at module load. The Settings page persists + reloads, so a fresh page
// load re-reads localStorage here.
const overrides = loadOverrides();

/** The `.env` value (or built-in default) for a key, ignoring any override. */
export function envDefault (key: string): string {
  const d = byKey(key);
  if (!d) return '';
  const v = ENV_DEFAULTS[key];
  return v === undefined || v === '' ? d.default : v;
}

/** True when a saved override is shadowing the `.env`/built-in default. */
export function isOverridden (key: string): boolean {
  const o = overrides[key];
  return o !== undefined && o !== '';
}

/** Effective raw string value: override ?? env default ?? built-in default. */
export function rawValue (key: string): string {
  const o = overrides[key];
  if (o !== undefined && o !== '') return o;
  return envDefault(key);
}

export function getBool (key: string): boolean {
  return rawValue(key) === 'true';
}

export function getEnum<T extends string> (key: string, allowed: readonly T[], fallback: T): T {
  const v = rawValue(key);
  return (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

/** A snapshot of every effective value — handy for seeding the Settings form. */
export function effectiveSnapshot (): Record<string, string> {
  const out: Record<string, string> = {};
  for (const d of SETTING_DESCRIPTORS) out[d.key] = rawValue(d.key);
  return out;
}

/** Merge a patch of overrides and persist. Caller reloads to apply. */
export function saveSettings (patch: Overrides): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const next: Overrides = { ...overrides, ...patch };
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Drop all overrides, reverting to `.env`/built-in defaults. Caller reloads. */
export function clearSettings (): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(SETTINGS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
